import { IDENTITIES_KEYS, } from './types';
import { awaitStorageItem, clearStorage, getStorage, getStorageItem, initStorage, updateStorage, } from './modules/storage';
import { subscribe } from './modules/subscribing';
import { createValuesObjects, prepareIdentityStorageObject, sanitizeIdentities } from './modules/general';
import { DEFAULT_GET_RULES, EVENT_NAME_READY } from './modules/consts';
import { getNullableObject, isIdentitiesSimplifiedObject } from './modules/utils';
import { rulesDefaultsAll } from './modules/getrules';
import { createValueForSpecial } from './modules/specials';
import { getVersion } from './modules/versioning';
import { includeOnlyWhitelisted } from './modules/whitelisting';
import { sendEvent } from './modules/events';
import { pullInitialIdentities } from './modules/config';
import { browserCleanUp } from './modules/cleanup';
import { getInvalidUpdatesForBe, getInvalidUpdates } from './modules/invalidUpdates';
// Ensures that `window.sznIVA.IS` is present right after the script is loaded
prepareIdentityStorageObject();
/**
 * Init global object. Clean up any stale browsers's data (cookies, LS, SS, ...)
 */
const _prepareInit = () => {
    browserCleanUp();
    return sanitizeIdentities({}, createValuesObjects(pullInitialIdentities(), createValueForSpecial));
};
initStorage(false, _prepareInit());
// export init (usable for testing purposes)
export const _init = (force = false, mergeInit = false) => initStorage(force, mergeInit ? _prepareInit() : {});
/**
 * Get specific identity
 * @param identificator - Identity we want to retrieve (can be scoped -> "consent.name")
 * @param rules - Rules (rules for identity to be retrieved)
 * - rules.rich - If TRUE, whole identity object is retrieved (otherwise just value)
 * - rules.justSettled - If TRUE, identity need to be "settled"
 * - rules.cacheEnough - If TRUE, "cache" value is considered to be enough
 */
export const getIdentity = (identificator, rules = { rich: false, justSettled: true }) => { var _a; return (_a = getStorageItem(identificator, rules)) !== null && _a !== void 0 ? _a : null; };
/**
 * Get specific identity as promise
 * @param identificator - Identity we want to retrieve
 * @param rules - Rules (rules for identity to be retrieved)
 * - rules.rich - If TRUE, whole identity object is retrieved (otherwise just value)
 * - rules.cacheEnough - If TRUE, "cache" value is considered to be enough
 */
export const getIdentitySettled = (identificator, rules = { rich: false }, doNotTimeout = false) => awaitStorageItem(identificator, rules, doNotTimeout);
/**
 * Get all identities - with global + granular "get" rules
 * @param rules - global rules for all identities
 * - rich - if TRUE, whole identity objects are retrieved (otherwise just values)
 * - justSettled - if TRUE, identities need to be "settled"
 * - cacheEnough - if TRUE, "cache" values are considered to be enough
 * @param rulesGranular - separated rules for each identity (higher priority)
 * @param nullable - in case of empty object -> return null
 */
export const getIdentities = (rules = DEFAULT_GET_RULES, rulesGranular = {}, nullable = false) => {
    const rls = rulesDefaultsAll(rules || DEFAULT_GET_RULES, rulesGranular || {});
    return getNullableObject(getStorage(rls), nullable);
};
/**
 * Update identities ("updated" and "state" flags are updated for each submited identity)
 * - Native identities (cookies, global variables, ...) are intact
 * @param identities - Identities object, which will be merged with current identity storage (if values differs)
 */
export const updateIdentities = (identities) => updateStorage(createValuesObjects(identities, createValueForSpecial));
/**
 * Purge identities from storage (mainly for test reasons)
 */
export const clearIdentities = (identities = Object.values(IDENTITIES_KEYS)) => clearStorage(identities);
/**
 * Subscribe to identities changes
 * @param callback - Function to be called with data:
 * - data.identities: {...current storage state...}
 * - data.changes: [...array of keys of changed identitites]
 * @returns Function for unsubscribing
 */
export const subscribeIdentities = (callback, rich = false) => subscribe(callback, rich);
/**
 * Prepare data for backend (ids).
 * Include only whitelisted keys (if any) , _version and _metadata (if includeMetadata = true)
 */
export const prepareIdsForBe = (identities, config) => {
    const { includeMetadata = false } = config || {};
    const isIdentityObjectFull = isIdentitiesSimplifiedObject(identities) && Object.keys(identities).length;
    const _invalids = getInvalidUpdatesForBe();
    // do we have any data to be structured / versioned?
    if (isIdentityObjectFull || (includeMetadata && _invalids)) {
        return Object.assign(Object.assign(Object.assign({}, (isIdentityObjectFull ? includeOnlyWhitelisted(identities) : {})), (includeMetadata && _invalids ? { _metadata: { _invalids } } : {})), { _version: getVersion() });
    }
    return null;
};
/**
 * Get "viable" login identity ("state", "rusId / said", "premium" has to be set)
 */
export const getViableLoginIdentity = (rules = { rich: false }, doNotTimeout = false) => {
    return awaitStorageItem(IDENTITIES_KEYS.login, rules, doNotTimeout, (identity) => {
        const login = ((identity === null || identity === void 0 ? void 0 : identity.value) || {});
        return (!!login.state &&
            !!(login.advert_uid || login.said || login.uid) &&
            (typeof login.premium === 'boolean' || typeof login.sbr === 'boolean'));
    }, true);
};
export const getInvalidUpdatesLog = () => getInvalidUpdates();
/**
 * Expose to global object
 */
const OUTPUT = {
    _init,
    getIdentity,
    getIdentitySettled,
    getIdentities,
    updateIdentities,
    clearIdentities,
    subscribeIdentities,
    prepareIdsForBe,
    getViableLoginIdentity,
    getInvalidUpdatesLog,
};
const _metadata = {
    // replaced by webpack (using current or semanticRelease "future")
    v: '2.41.0',
};
prepareIdentityStorageObject(Object.assign(Object.assign({}, OUTPUT), { _metadata }));
//Let outside world know that IS is ready
sendEvent(EVENT_NAME_READY);
export default OUTPUT;
